import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2, FileText, ExternalLink } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { FileUploader, FileChip } from "../components/module-widgets";

const DESC = {
  SEQ: "ISO, Health & Safety consultancy and accredited training.",
  FSS: "Fire safety servicing, inspections and maintenance.",
  Amden: "Business consultancy, digital transformation and AI.",
};

const REQ_STATUS = {
  new: { label: "Submitted", cls: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
  quote_sent: { label: "Quote received — action needed", cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  approved: { label: "Approved", cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  declined: { label: "Declined", cls: "bg-red-500/10 text-red-600 dark:text-red-400" },
};

export default function Marketplace() {
  const [data, setData] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [approving, setApproving] = useState(null);
  const [po, setPo] = useState({ po_file: null, po_note: "" });

  const loadReq = () => api.get("/marketplace/requests").then((r) => setRequests(r.data));
  useEffect(() => {
    api.get("/marketplace").then((r) => setData(r.data));
    loadReq();
  }, []);

  const request = async () => {
    await api.post("/marketplace/requests", { provider: selected.provider, service: selected.service, notes });
    toast.success("Request sent — a consultant will be in touch.");
    setSelected(null); setNotes("");
    loadReq();
  };

  const approveQuote = async () => {
    try {
      await api.patch(`/marketplace/requests/${approving.id}`, { status: "approved", po_file: po.po_file, po_note: po.po_note });
      toast.success("Quote approved — we'll get started.");
      setApproving(null); setPo({ po_file: null, po_note: "" });
      loadReq();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const decide = async (id, status) => {
    try {
      await api.patch(`/marketplace/requests/${id}`, { status });
      toast.success(status === "approved" ? "Quote approved — we'll get started." : "Quote declined.");
      loadReq();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div>
      <PageHeader title="Service Marketplace" subtitle="Request expert services from SEQ, FSS and Amden — every request becomes a tracked lead." />
      <Tabs defaultValue="SEQ">
        <TabsList className="mb-5 rounded-full">
          {Object.keys(data).map((p) => <TabsTrigger key={p} value={p} className="rounded-full" data-testid={`market-tab-${p}`}>{p}</TabsTrigger>)}
        </TabsList>
        {Object.entries(data).map(([provider, info]) => (
          <TabsContent key={provider} value={provider}>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-3 w-3 rounded-full" style={{ background: info.color }} />
              <p className="text-sm text-muted-foreground">{DESC[provider]}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {info.services.map((s) => (
                <Card key={s} className="group flex items-center justify-between rounded-2xl border-border p-5 transition-all hover:-translate-y-1 hover:shadow-md">
                  <div>
                    <div className="font-medium">{s}</div>
                    <div className="text-xs text-muted-foreground">{provider}</div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setSelected({ provider, service: s })} data-testid={`request-${s.toLowerCase().replace(/\s/g, "-")}`}>
                    Request <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </Card>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {requests.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 font-heading text-lg font-bold">Your Requests</h2>
          <Card className="rounded-2xl border-border">
            {requests.map((r) => {
              const s = REQ_STATUS[r.status] || REQ_STATUS.new;
              return (
                <div key={r.id} className="border-b border-border/60 px-5 py-4 last:border-0" data-testid={`my-request-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div><div className="text-sm font-medium">{r.service}</div><div className="text-xs text-muted-foreground">{r.provider} · by {r.requested_by}</div></div>
                    <Badge className={`rounded-full ${s.cls}`}>{s.label}</Badge>
                  </div>
                  {r.status === "quote_sent" && (
                    <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quote received</div>
                      <div className="mt-1 text-sm font-semibold">{r.quote_amount || "See details"}</div>
                      {r.quote_note && <div className="mt-0.5 text-sm text-muted-foreground">{r.quote_note}</div>}
                      {(r.quote_file || r.quote_link) && (
                        <a href={r.quote_file ? fileUrl(r.quote_file.path) : r.quote_link} target="_blank" rel="noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium transition-colors hover:border-[hsl(var(--accent))] hover:text-[hsl(var(--accent))]" data-testid={`view-quote-doc-${r.id}`}>
                          <FileText className="h-3.5 w-3.5" /> {r.quote_file ? (r.quote_file.filename || "View quote document") : "View quote link"}
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </a>
                      )}
                      <div className="mt-3 flex gap-2">
                        <Button size="sm" className="rounded-full text-xs" onClick={() => { setPo({ po_file: null, po_note: "" }); setApproving(r); }} data-testid={`approve-quote-${r.id}`}><CheckCircle2 className="mr-1 h-3 w-3" /> Approve Quote</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => decide(r.id, "declined")} data-testid={`decline-quote-${r.id}`}>Decline</Button>
                      </div>
                    </div>
                  )}
                  {r.status === "approved" && (r.po_file || r.po_note) && (
                    <div className="mt-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Your PO / details</div>
                      {r.po_note && <div className="mt-1 text-sm text-muted-foreground">{r.po_note}</div>}
                      {r.po_file && <div className="mt-2"><FileChip file={r.po_file} /></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Request: {selected?.service}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Provided by <span className="font-medium text-foreground">{selected?.provider}</span>. Add any details and a consultant will follow up.</p>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Tell us what you need…" data-testid="request-notes" /></div>
          </div>
          <DialogFooter><Button onClick={request} className="rounded-full" data-testid="submit-request-btn"><CheckCircle2 className="mr-2 h-4 w-4" /> Send Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Approve Quote — {approving?.service}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Approving <span className="font-medium text-foreground">{approving?.quote_amount || "this quote"}</span> from {approving?.provider}. Optionally attach a Purchase Order (PDF) and add any details.</p>
            <div className="space-y-1.5"><Label>PO number / notes (optional)</Label><Textarea rows={3} value={po.po_note} onChange={(e) => setPo({ ...po, po_note: e.target.value })} placeholder="e.g. PO #4821, invoice to accounts@…, preferred start date…" data-testid="po-note" /></div>
            <div className="space-y-2">
              <Label>Purchase order document (optional)</Label>
              {po.po_file && <FileChip file={po.po_file} />}
              <FileUploader label={po.po_file ? "Replace PO" : "Upload PO (PDF)"} testid="po-upload" onUploaded={(f) => setPo({ ...po, po_file: f })} />
            </div>
          </div>
          <DialogFooter><Button onClick={approveQuote} className="rounded-full" data-testid="confirm-approve-btn"><CheckCircle2 className="mr-2 h-4 w-4" /> Confirm Approval</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
